// cash.js — Cash management, P&L tracking, and reserve fund operations
// Extracted from zagx-app/index.html

import { S, save, stampItem, softDelete, tombstoneId } from '../core/state.js';
import { uid, now, fU, fT, fByCur, ds, esc, f, pc, VALID_CURRENCIES, toUSD, realizedUSD, isInterestRealized, dividendUSD, usdToTRY, isUsableAmount, AMOUNT_CAP } from '../core/utils.js';
import { reserveTargetUSD, strategicReserveUSD, projectedCashTRY, tryCashPosture, cashTRYNow, stampConfirmedBeforeReading, cashAnchorDay } from '../modules/cashflow.js';
import { tdAccrued } from '../core/math.js';
import { _safeDestroy, _safeChart } from '../ui/charts.js';
import { toast, clMo } from '../ui/layout.js';
import { uiConfirm, uiPrompt } from '../ui/dialog.js';
import { markDirty } from '../app/render-loop.js';
import { classifyCashTx, TX_CLASS, DEPOSIT_REASONS, WITHDRAW_REASONS, reasonLabel, cashTxOrigin, FX_PURPOSE, FX_PURPOSE_LABEL } from '../core/capital.js';

// === VARIABLES ===
let cTxTp = null; // Current cash transaction type ('deposit' or 'withdraw')
let ch3 = null;   // P&L chart instance
// View state only — deliberately NOT persisted. It says how much of the ledger is on screen,
// which is a property of this visit, not of the book.
let _cTxShowAll = false;

/** Flip between the latest 50 and the whole ledger. */
export function cTxToggleAll() {
  _cTxShowAll = !_cTxShowAll;
  renderC();
}

const _cbTypeLabels = {
  trade_exit: 'Trade Exits',
  trade_open: 'Trade Opens',
  deposit: 'Deposits',
  withdraw: 'Withdrawals',
  td_open: 'TD Opens',
  td_close: 'TD Closes',
  td_mature: 'TD Matured',
  td_topup: 'TD Top-ups',
  portfolio_buy: 'Portfolio Buys',
  fx_convert: 'Currency Conversions'
};

const _cbColors = {
  trade_exit: '#7ff0c4',
  deposit: '#8b5cf6',
  td_close: '#a78bfa',
  td_mature: '#4DC9F6',
  trade_open: '#FF6B8A',
  td_open: '#FF8C42',
  withdraw: '#FF6B8A',
  td_topup: '#D4A853',
  portfolio_buy: '#D4A853',
  fx_convert: '#4DC9F6'
};

let _cbCharts = {};

// === CASH SUMMARY AND BALANCES RENDERER ===
export function renderC() {
  const cashUSD = (S.cash?.USD || 0);
  // What the TRY account holds NOW. The hero used to print the last bank reading plus every
  // ledger move since, while the panel four rows below it printed the real figure.
  const _tryNowTRY = cashTRYNow();
  const cashTRYinUSD = S.fxRate ? _tryNowTRY / S.fxRate : 0;
  const cashEURinUSD = toUSD(S.cash?.EUR || 0, 'EUR');
  const totalCashUSD = cashUSD + cashTRYinUSD + cashEURinUSD;
  const rtUSD = S.realized.reduce((a, r) => a + (realizedUSD(r)), 0);

  // Negative balance warnings
  const negWarns = [];
  if ((S.cash?.USD || 0) < 0) negWarns.push('USD: ' + fU(S.cash.USD));
  if (_tryNowTRY < 0) negWarns.push('TRY: ' + fT(_tryNowTRY));
  if ((S.cash?.EUR || 0) < 0) negWarns.push('EUR: €' + f(S.cash.EUR));
  const _rfObj = typeof S.reserveFund === 'object' ? S.reserveFund : { TRY: S.reserveFund || 0 };
  Object.entries(_rfObj).forEach(([c, v]) => { if (v < 0) negWarns.push('RF ' + c + ': ' + f(v)); });

  const negBanner = negWarns.length ? `<div style="grid-column:1/-1;background:var(--red-bg);border:1px solid var(--red);border-radius:8px;padding:10px 14px;font-size:12px;color:var(--red)">⚠ Negative balance detected: ${negWarns.join(' · ')} — review transactions</div>` : '';

  // Unclassified rows, at the TOP and in FULL.
  //
  // The first attempt put the label control inline on each row. It was technically there
  // and practically invisible: the table shows the newest 50 of 84 rows, so the ₺500,000
  // row sat at index 53 and never rendered at all, and the two that did render were 2,600px
  // down the page. A control the owner has to go looking for is the same failure this whole
  // audit is about, one level up. So: every unclassified row, above the fold, whether or not
  // the table below happens to reach it. (audit 2026-08-02)
  const _unclassified = (S.cashTxs || []).filter(t => t && t.id && classifyCashTx(t).cls === TX_CLASS.UNKNOWN);
  const clsBanner = _unclassified.length ? `<div style="background:rgba(251,146,60,.10);border:1px solid var(--ora);border-radius:10px;padding:12px 14px;margin:0 0 14px;backdrop-filter:blur(8px)">
    <div style="font-size:12px;color:var(--ora);font-weight:600">${_unclassified.length} transaction${_unclassified.length > 1 ? 's' : ''} could not be classified</div>
    <div style="font-size:11px;color:var(--t2);margin-top:4px;line-height:1.5">They are held out of the capital figures — not counted, not ignored. Until every row is labelled, NET GROWTH and the benchmark alpha stay suppressed rather than shown wrong. Only you know what these were.</div>
    <div style="margin-top:10px;display:flex;flex-direction:column;gap:6px">
      ${_unclassified.map(t => {
    const cur = t.currency || 'USD';
    return `<div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;font-size:11px;padding:6px 8px;background:rgba(255,255,255,.03);border-radius:6px">
        <span style="color:var(--t3);min-width:74px">${ds(t.date)}</span>
        <span class="badge b-${t.type === 'deposit' ? 'open' : 'stopped'}" style="font-size:9px">${esc(t.type)}</span>
        <span class="${t.amount >= 0 ? 'pos' : 'neg'}" style="min-width:96px;text-align:right;font-weight:600">${fByCur(cur, Math.abs(t.amount || 0))}</span>
        <span style="color:var(--t2);flex:1;min-width:120px">${esc(t.note || '(no note)')}</span>
        <select data-action-change="setCashTxClass" data-args='["${t.id}"]' style="font-size:11px;padding:3px 6px">
          <option value="">Label it…</option>
          <option value="${TX_CLASS.EXTERNAL_IN}">Money in (capital)</option>
          <option value="${TX_CLASS.EXTERNAL_OUT}">Money out (spent)</option>
          <option value="${TX_CLASS.INTERNAL}">Internal move</option>
          <option value="${TX_CLASS.MIRROR}">Mirror (no money moved)</option>
        </select>
      </div>`;
  }).join('')}
    </div>
  </div>` : '';

  // #cSum is the LEGACY grid — the Money redesign hides it (display:none), so anything
  // rendered into it is invisible no matter how prominent it looks in the source. That is
  // where the first version of this banner went. It now has its own slot in the visible
  // shell, directly under the header. (audit 2026-08-02)
  const _clsSlot = document.getElementById('caUnclassified');
  if (_clsSlot) _clsSlot.innerHTML = clsBanner;

  document.getElementById('cSum').innerHTML = negBanner + `
  <div class="neo-cell"><div class="l">Cash (USD)</div><div class="v ${(S.cash?.USD || 0) < 0 ? 'neg' : ''}">${fU(S.cash?.USD || 0)}</div></div>
  <div class="neo-cell"><div class="l">Cash (TRY)</div><div class="v ${_tryNowTRY < 0 ? 'neg' : ''}">${fT(_tryNowTRY)}</div></div>
  <div class="neo-cell"><div class="l">Cash (EUR)</div><div class="v ${(S.cash?.EUR || 0) < 0 ? 'neg' : ''}">€${f(S.cash?.EUR || 0)}</div></div>
  <div class="neo-cell"><div class="l">Total Cash (USD)</div><div class="v">${fU(totalCashUSD)}</div><div class="s">${fT(usdToTRY(totalCashUSD))}</div></div>
  <div class="neo-cell"><div class="l">Realized P&L</div><div class="v ${pc(rtUSD)}">${fU(rtUSD)}</div></div>`;

  try { _caNeo(); } catch (e) { console.error('[ZAGX] _caNeo failed:', e); }

  const _userTxTypes = new Set(['deposit', 'withdraw']);
  // Sort before slicing. cashTxs is built with unshift, so it is ROUGHLY newest-first but
  // not actually ordered — live state had an out-of-order pair, which means "the latest 50"
  // was not the latest 50.
  const _txView = S.cashTxs.slice().sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  // SHOWING 50 OF 119 AND SAYING NOTHING IS A SILENT DEGRADATION.
  //
  // This was a bare `.slice(0, 50)` with no count, no pagination, no filter and no control of
  // any kind. Measured on the live book 2026-08-14: 69 of 119 rows were unreachable — everything
  // older than 2026-05-24, carrying **₺9,919,900 of absolute lira value, 83% of the ledger**,
  // including both ₺1.46M liability-payment rows and the ₺605,042.12 bonus. The page looked
  // complete, so the door badges shipped that same day were invisible for exactly the rows they
  // were built for.
  //
  // The cap itself is worth keeping — 119 rows of innerHTML is cheap, 2,000 (the `_CAPS` limit)
  // is not. So: cap by default, SAY SO, and give him the rest on one click.
  const _txLimit = _cTxShowAll ? _txView.length : 50;
  const _txHidden = Math.max(0, _txView.length - _txLimit);
  const _txMoreSlot = document.getElementById('cTxMore');
  if (_txMoreSlot) {
    _txMoreSlot.innerHTML = _txView.length <= 50 ? '' : (_txHidden > 0
      ? `<div style="padding:6px 2px;font-size:11px;color:var(--t3)">Showing <strong>${_txLimit}</strong> of <strong>${_txView.length}</strong> rows — ${_txHidden} older ${_txHidden === 1 ? 'row is' : 'rows are'} hidden. <button class="btn btn-s" style="font-size:10px;margin-left:6px" data-action="cTxToggleAll">Show all</button></div>`
      : `<div style="padding:6px 2px;font-size:11px;color:var(--t3)">Showing all <strong>${_txView.length}</strong> rows. <button class="btn btn-s" style="font-size:10px;margin-left:6px" data-action="cTxToggleAll">Show latest 50</button></div>`);
  }
  document.getElementById('cTxB').innerHTML = _txView.slice(0, _txLimit).map(t => {
    const cur = t.currency || 'USD';
    const fc = v => fByCur(cur, v);
    // PROVENANCE, not type. `_userTxTypes` says what the row DOES to a balance; it says
    // nothing about who wrote it, and 38 of the 47 rows it admitted on 2026-08-14 were system
    // mirrors of a dividend, an OTI, a liability payment or a monthly reconciliation. Each has
    // a twin elsewhere in the state, and reversing the cash while the twin stands is the same
    // one-legged write this codebase keeps finding — pointing backwards. (MOVE-003)
    const _origin = cashTxOrigin(t);
    const canDel = _userTxTypes.has(t.type) && t.id && !_origin.system;
    const _originBadge = _origin.system
      ? `<span class="badge" style="background:rgba(148,163,184,.14);color:var(--t3);font-size:9px;padding:1px 5px" title="${esc(_origin.why)} — undo it at ${esc(_origin.door)}">${esc(_origin.door)}</span>`
      : '';
    const auditBadge = t._audit ? ' <span class="badge" style="background:rgba(167,139,250,.15);color:var(--pur);font-size:9px;padding:1px 5px;margin-left:4px">Audit</span>' : '';
    // A row capital.js cannot classify is held out of BOTH capital totals — it is not
    // counted and not ignored, it is parked. Parking it silently would be the same failure
    // in a new coat, so the row says so and offers the four answers. (audit 2026-08-02)
    const _cls = classifyCashTx(t);
    const _rowUnclassified = _cls.cls === TX_CLASS.UNKNOWN && t.id;
    const _rowStyle = t._audit ? ' style="opacity:.7"'
      : _rowUnclassified ? ' style="box-shadow:inset 3px 0 0 var(--ora)"' : '';
    const _clsCell = _rowUnclassified
      ? `<select data-action-change="setCashTxClass" data-args='["${t.id}"]' title="${esc(_cls.rule)}" style="font-size:10px;padding:2px 4px;max-width:120px">
           <option value="">Unclassified…</option>
           <option value="${TX_CLASS.EXTERNAL_IN}">Money in (capital)</option>
           <option value="${TX_CLASS.EXTERNAL_OUT}">Money out (spent)</option>
           <option value="${TX_CLASS.INTERNAL}">Internal move</option>
           <option value="${TX_CLASS.MIRROR}">Mirror (no money moved)</option>
         </select>`
      : '';
    return `<tr${_rowStyle}><td>${ds(t.date)}</td><td><span class="badge b-${t.type === 'deposit' ? 'open' : t.type === 'withdraw' ? 'stopped' : 'closed'}">${t.type}</span>${auditBadge}</td><td>${cur}</td><td class="tr ${t.amount >= 0 ? 'pos' : 'neg'}">${fc(Math.abs(t.amount))}</td><td style="color:var(--t2)">${esc(t.note || '')}${_clsCell ? '<div style="margin-top:4px">' + _clsCell + '</div>' : ''}</td><td>${canDel ? '<button class="btn btn-s btn-d" style="font-size:9px;padding:2px 6px" data-action="delCashTx" data-args=\'["' + t.id + '"]\'>Del</button>' : _originBadge}</td></tr>`;
  }).join('') || '<tr><td colspan="6" class="neo-empty">No cash transactions yet</td></tr>';

  document.getElementById('rB').innerHTML = S.realized.slice(0, 50).map(r => {
    const cur = r.currency || 'USD';
    const fc = v => fByCur(cur, v);
    const pU = realizedUSD(r);
    return `<tr><td>${ds(r.date)}</td><td><strong>${esc(r.ticker)}</strong></td><td>${cur}</td><td class="tr">${f(r.qty)}</td><td class="tr">${fc(r.entry)}</td><td class="tr">${fc(r.exit)}</td><td class="tr ${pc(r.pnl)}">${fc(r.pnl)}</td><td class="tr ${pc(pU)}">${fU(pU)}</td></tr>`;
  }).join('') || '<tr><td colspan="8" class="neo-empty">No realized trades yet</td></tr>';

  renderPnlCh();
  renderRF();
  _cashBreakdown();
}

// === CASH BREAKDOWN ANALYSIS ===
function _cashBreakdown() {
  const el = document.getElementById('cashBreakdownContent');
  if (!el) return;
  if (!S.cashTxs || S.cashTxs.length === 0) {
    el.innerHTML = '<div class="neo-empty">No cash transactions yet.</div>';
    return;
  }

  const currencies = ['TRY', 'USD', 'EUR'].filter(c => (S.cash?.[c] || 0) !== 0 || S.cashTxs.some(t => t.currency === c));
  if (currencies.length === 0) {
    el.innerHTML = '';
    return;
  }

  let html = '';
  currencies.forEach(cur => {
    const txs = S.cashTxs.filter(t => (t.currency || 'USD') === cur && !t._audit);
    if (txs.length === 0) return;

    const byType = {};
    txs.forEach(t => {
      const tp = t.type || 'other';
      if (!byType[tp]) byType[tp] = { in: 0, out: 0, net: 0, count: 0 };
      const amt = t.amount || 0;
      if (amt >= 0) byType[tp].in += amt;
      else byType[tp].out += amt;
      byType[tp].net += amt;
      byType[tp].count++;
    });

    const fc = v => fByCur(cur, v);
    const types = Object.keys(byType).sort((a, b) => byType[b].net - byType[a].net);
    const totalIn = types.reduce((s, t) => s + byType[t].in, 0);
    const totalOut = types.reduce((s, t) => s + byType[t].out, 0);

    const rows = types.map(tp => {
      const d = byType[tp];
      const label = _cbTypeLabels[tp] || tp;
      const clr = _cbColors[tp] || 'rgba(255,255,255,.3)';
      return `<tr>
        <td><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${clr};margin-right:6px"></span>${label}</td>
        <td class="tr" style="color:var(--t2)">${d.count}</td>
        <td class="tr pos">${d.in > 0 ? '+' + fc(d.in) : '—'}</td>
        <td class="tr neg">${d.out < 0 ? fc(d.out) : '—'}</td>
        <td class="tr ${d.net >= 0 ? 'pos' : 'neg'}" style="font-weight:600">${fc(d.net)}</td>
      </tr>`;
    }).join('');

    const canvasId = 'cbChart_' + cur;
    html += `<div>
      <div style="font-size:13px;font-weight:700;letter-spacing:1px;color:var(--t2);margin-bottom:10px">${cur}</div>
      <div style="display:grid;grid-template-columns:180px 1fr;gap:16px;align-items:start">
        <div style="height:180px"><canvas id="${canvasId}"></canvas></div>
        <div class="tw"><table style="font-size:12px"><thead><tr><th>Source</th><th class="tr">Txns</th><th class="tr">Inflow</th><th class="tr">Outflow</th><th class="tr">Net</th></tr></thead><tbody>${rows}</tbody>
          <tfoot><tr style="border-top:1px solid var(--brd);font-weight:700"><td>Total</td><td class="tr" style="color:var(--t2)">${txs.length}</td><td class="tr pos">+${fc(totalIn)}</td><td class="tr neg">${fc(totalOut)}</td><td class="tr ${(totalIn + totalOut) >= 0 ? 'pos' : 'neg'}">${fc(totalIn + totalOut)}</td></tr></tfoot></table></div>
      </div>
    </div>`;
  });

  el.innerHTML = html;

  // Render doughnut charts per currency (inflows only — "where did cash come from")
  currencies.forEach(cur => {
    const canvasId = 'cbChart_' + cur;
    const cvs = document.getElementById(canvasId);
    if (!cvs) return;

    const txs = S.cashTxs.filter(t => (t.currency || 'USD') === cur && !t._audit);
    const byType = {};
    txs.forEach(t => {
      const tp = t.type || 'other';
      if (!byType[tp]) byType[tp] = 0;
      if ((t.amount || 0) > 0) byType[tp] += (t.amount || 0);
    });

    const labels = Object.keys(byType).filter(k => byType[k] > 0);
    if (labels.length === 0) return;

    const data = labels.map(k => byType[k]);
    const colors = labels.map(k => _cbColors[k] || 'rgba(255,255,255,.3)');
    _cbCharts[cur] = _safeDestroy(_cbCharts[cur]);
    _cbCharts[cur] = _safeChart(cvs.id, {
      type: 'doughnut',
      data: {
        labels: labels.map(k => _cbTypeLabels[k] || k),
        datasets: [{
          data,
          backgroundColor: colors,
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: item => {
                const fc = v => fByCur(cur, v);
                return ' ' + item.label + ': ' + fc(item.raw);
              }
            }
          }
        }
      }
    });
  });
}

// === P&L CUMULATIVE CHART ===
export function renderPnlCh() {
  if (!S.realized.length) {
    ch3 = _safeDestroy(ch3);
    const cvs = document.getElementById('chPnl');
    if (cvs) {
      const ctx = cvs.getContext('2d');
      ctx.clearRect(0, 0, cvs.width, cvs.height);
      ctx.fillStyle = '#484f58';
      ctx.font = '12px sans-serif';
      ctx.fillText('No realized trades yet.', 20, 80);
    }
    return;
  }

  let cum = 0;
  const data = S.realized.map(r => {
    const pU = realizedUSD(r);
    cum += pU;
    return { x: ds(r.date), y: cum };
  });

  const labels = data.map(d => d.x);
  const vals = data.map(d => d.y);
  const clr = cum >= 0 ? '#7ff0c4' : '#FF6B8A';
  const bg = cum >= 0 ? 'rgba(110,238,180,.1)' : 'rgba(255,107,138,.1)';

  if (ch3 && ch3.canvas) {
    ch3.data.labels = labels;
    ch3.data.datasets[0].data = vals;
    ch3.data.datasets[0].borderColor = clr;
    ch3.data.datasets[0].backgroundColor = bg;
    ch3.update('none');
  } else {
    ch3 = _safeDestroy(ch3);
    ch3 = _safeChart('chPnl', {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: 'P&L',
          data: vals,
          borderColor: clr,
          backgroundColor: bg,
          fill: true,
          tension: 0.3,
          pointRadius: 3
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: {
            ticks: { color: '#4a5568' },
            grid: { color: 'rgba(30,42,58,.3)' }
          },
          y: {
            ticks: {
              color: '#4a5568',
              callback: v => '$' + v.toLocaleString()
            },
            grid: { color: 'rgba(30,42,58,.3)' }
          }
        }
      }
    });
  }
  // Render dividend list if available (via window to avoid circular import)
  if (typeof window.renderDividendList === 'function') window.renderDividendList();
}

// === CASH TRANSACTION MODAL HANDLERS ===

// Built-in reason categories — DERIVED from REASON_CLASS in capital.js, not restated here.
// These were two hand-written arrays that had to stay in step with the classifier and with the
// <option> list in index.html, and nothing made them. (2026-08-14)
const _DEPOSIT_REASONS = DEPOSIT_REASONS;
const _WITHDRAW_REASONS = WITHDRAW_REASONS;

/** Collect custom reasons from cashTx history (user-created, not system-generated) */
function _getCustomReasons() {
  const customs = new Set();
  const builtIn = new Set([..._DEPOSIT_REASONS, ..._WITHDRAW_REASONS]);
  const systemPrefixes = ['dividend:', 'drip reinvest:', 'expense:', 'undo expense:', 'liability payment:', 'portfolio_buy:', 'monthly_'];
  (S.cashTxs || []).forEach(tx => {
    const r = (tx.reason || '').trim();
    if (r && !builtIn.has(r)) {
      // Skip system-generated
      const noteLow = (tx.note || '').toLowerCase();
      const isSystem = systemPrefixes.some(p => noteLow.startsWith(p));
      if (!isSystem) customs.add(r);
    }
  });
  return [...customs].sort();
}

/** Populate reason dropdown based on tx type */
function _populateReasons(tp) {
  const sel = document.getElementById('cReason');
  const customGroup = document.getElementById('cReasonCustom');
  const depGroup = document.getElementById('cReasonDeposit');
  const witGroup = document.getElementById('cReasonWithdraw');

  // ⚠ THE BUILT-IN OPTIONS ARE PAINTED FROM THE CLASSIFIER, NOT WRITTEN IN THE HTML.
  //
  // They used to be ten hand-typed `<option>` elements in index.html — the third copy of a
  // vocabulary that already lived in `REASON_CLASS` and in this file, and the copy nothing
  // checked. The comment in capital.js claimed `reason-vocabulary.test.js` pinned them; that
  // file has never existed (found 2026-08-16, rule #41 — a doctrine can cite a guard that is
  // not there). The drift it would have hidden is not cosmetic in either direction: a reason
  // added to the classifier and not to the HTML is a feature nobody can reach, and a reason
  // left in the HTML after the classifier drops it is a row that classifies as UNKNOWN, which
  // flips `reconciled` to false and zeroes that day's `netExtCapital`.
  //
  // The labels come from `reasonLabel`, because four of them say more than the stored value
  // does and deleting that hint to win the de-duplication would have been a worse trade.
  const opts = list => list.map(r => `<option value="${esc(r)}">${esc(reasonLabel(r))}</option>`).join('');
  if (depGroup && !depGroup.dataset.painted) { depGroup.innerHTML = opts(DEPOSIT_REASONS); depGroup.dataset.painted = '1'; }
  if (witGroup && !witGroup.dataset.painted) { witGroup.innerHTML = opts(WITHDRAW_REASONS); witGroup.dataset.painted = '1'; }

  // Show/hide relevant groups
  if (depGroup) depGroup.style.display = tp === 'deposit' ? '' : 'none';
  if (witGroup) witGroup.style.display = tp === 'withdraw' ? '' : 'none';

  // Populate custom reasons from history
  if (customGroup) {
    const customs = _getCustomReasons();
    customGroup.innerHTML = customs.map(c => `<option value="${esc(c)}">${esc(c)}</option>`).join('');
    customGroup.style.display = customs.length ? '' : 'none';
    customGroup.label = 'Previously Used';
  }

  sel.value = '';
}

/**
 * Read the real TL balances off the banks and make `S.cash.TRY` equal them.
 *
 * The gap this closes, measured 2026-08-08: app ₺234,384.75, banks ₺336,800. Five months of
 * salary reached the ledger as `_audit` mirror rows — recorded, never credited — while the
 * household spending they funded was never debited at all, because `cfToggle` only debits for
 * `paidBy: 'savings'`. Neither side of that was wrong on its own; there was simply no number
 * in the app that claimed to be a bank balance, so nothing could disagree with one.
 *
 * The delta is booked as external capital, not as a gain. It is money that entered the system
 * and was never recorded entering, so `assets += d` has to be matched by `externalCapital += d`
 * or the capital identity would read the correction as ₺102,415 of investment performance.
 */
export async function reconcileCashTRY() {
  // Repair, do not dead-end. A missing list is a state that predates the field, not a
  // decision by the owner — so write the default in and say so, rather than refusing to run
  // and leaving him with a button that does nothing. (2026-08-08: this is exactly how it
  // shipped broken, because a SEED addition never reaches an existing state.)
  if (!S.settings) S.settings = {};
  if (!Array.isArray(S.settings.cashBanksTRY) || !S.settings.cashBanksTRY.filter(Boolean).length) {
    S.settings.cashBanksTRY = ['Garanti', 'Akbank'];
    toast('TRY banks were not configured — defaulted to Garanti + Akbank', 'a-w');
  }
  const banks = S.settings.cashBanksTRY.filter(Boolean);
  if (!S.cashRecon) S.cashRecon = { TRY: { banks: {}, at: null } };
  if (!S.cashRecon.TRY) S.cashRecon.TRY = { banks: {}, at: null };
  const prev = S.cashRecon.TRY.banks || {};

  const entered = {};
  for (const b of banks) {
    const raw = await uiPrompt(
      `${b} — current TRY current-account balance (exclude time deposits)`,
      prev[b] != null ? String(prev[b]) : '',
      { title: 'Reconcile TRY cash', okText: 'Next', inputType: 'number' }
    );
    if (raw === null) return; // cancelled — write nothing
    const n = parseFloat(String(raw).replace(/[^0-9.\-]/g, ''));
    if (!isFinite(n)) return toast(`${b}: not a number`, 'a-r');
    entered[b] = n;
  }

  const total = banks.reduce((s, b) => s + entered[b], 0);
  const before = S.cash?.TRY || 0;
  const delta = +(total - before).toFixed(2);

  const lines = banks.map(b => `${b}: ${fT(entered[b])}`).join('\n');
  const ok = await uiConfirm(
    `${lines}\n\nTotal: ${fT(total)}\nApp currently: ${fT(before)}\n` +
    (delta === 0
      ? '\nNo adjustment needed.'
      : `\nAdjustment: ${delta > 0 ? '+' : ''}${fT(delta)} — booked as ${delta > 0 ? 'external capital in' : 'external capital out'}, not as a gain.`)
  );
  if (!ok) return;

  if (delta !== 0) {
    S.cashTxs.unshift({
      id: uid(),
      date: now(),
      type: delta > 0 ? 'deposit' : 'withdraw',
      amount: delta,
      currency: 'TRY',
      txClass: delta > 0 ? TX_CLASS.EXTERNAL_IN : TX_CLASS.EXTERNAL_OUT,
      note: 'Cash reconciliation to bank balances (' + banks.map(b => b + ' ' + f(entered[b])).join(', ') + ')',
    });
    S.cash.TRY = total;
    lg('cash', 'Reconcile', `TRY ${f(before)} → ${f(total)} (${delta > 0 ? '+' : ''}${f(delta)})`);
  }

  const _at = now();
  S.cashRecon.TRY = { banks: entered, at: _at, total, lastDrift: delta };
  // Everything already ticked is inside the figure he just typed. Say so on the rows that
  // never got a payment stamp, or a bill DATED after today keeps testing as still-to-come and
  // comes out of the balance a second time. Loud, because it is a data write.
  const _stamped = stampConfirmedBeforeReading(_at);
  if (_stamped) lg('cashflow', 'Reconcile', _stamped + ' confirmed item(s) stamped as paid before this reading');
  save();
  markDirty('dash');
  renderC();
  toast(delta === 0 ? 'TRY cash already matched' : 'TRY cash reconciled', 'a-g');
}

export function openCTx(tp) {
  cTxTp = tp;
  document.getElementById('cTxTi').textContent = tp === 'deposit' ? 'Deposit' : 'Withdraw';
  document.getElementById('cAm').value = '';
  document.getElementById('cNo').value = '';
  document.getElementById('cCur').value = tp === 'deposit' ? 'TRY' : 'USD';
  document.getElementById('cTxDate').value = new Date().toISOString().slice(0, 10);
  document.getElementById('cReasonOtherWrap').style.display = 'none';
  document.getElementById('cReasonOther').value = '';
  _populateReasons(tp);
  document.getElementById('moCTx').classList.add('active');
}

/** Handle reason dropdown change — show/hide custom input */
export function onCashReasonChange() {
  const v = document.getElementById('cReason').value;
  const wrap = document.getElementById('cReasonOtherWrap');
  wrap.style.display = v === '__other__' ? '' : 'none';
  if (v === '__other__') document.getElementById('cReasonOther').focus();
}

export function saveCTx() {
  const am = parseFloat(document.getElementById('cAm').value);
  // Through the shared rule, so the chat door and this one cannot disagree about what a usable
  // amount is. This check was right and lived only here; ai-actions.js accepted Infinity.
  if (!isUsableAmount(am)) return toast(`Valid positive amount required, at most ${f(AMOUNT_CAP)}`, 'a-r');

  const cur = document.getElementById('cCur').value;
  if (!VALID_CURRENCIES.includes(cur)) return toast('Invalid currency', 'a-r');

  // Resolve reason
  let reason = document.getElementById('cReason').value;
  if (reason === '__other__') {
    reason = document.getElementById('cReasonOther').value.trim();
    if (!reason) return toast('Enter a custom reason', 'a-r');
  }
  if (!reason) return toast('Select a reason', 'a-r');

  const note = document.getElementById('cNo').value.trim();
  const dateVal = document.getElementById('cTxDate').value || new Date().toISOString().slice(0, 10);

  const fc = v => fByCur(cur, v);
  const signed = cTxTp === 'deposit' ? am : -am;

  // A TRY row dated on or before the last bank reading is already inside that reading: the
  // owner typed the balance AFTER the money moved. Moving S.cash.TRY again counts it twice, and
  // the next reconciliation absorbs the error as an "unexplained" correction of the opposite
  // sign. saveOTI has carried this rule since 2026-08-10; this door did not, and on 2026-10-13
  // a TRY 40,720 withdrawal dated 16 Sep, entered eleven days after the 2 Oct reading, took
  // the balance down by exactly that amount. Same shape as the OTI row: `_audit`, balance
  // untouched, the ledger still says the money left. USD and EUR carry no anchor.
  const anchorDay = cur === 'TRY' ? cashAnchorDay() : null;
  const insideReading = !!anchorDay && dateVal <= anchorDay;

  // F-021: Prevent negative cash balance on withdrawal
  if (!insideReading && cTxTp === 'withdraw' && am > (S.cash[cur] || 0)) {
    return toast('Insufficient ' + cur + ' balance (' + fc(S.cash[cur] || 0) + ')', 'a-r');
  }

  if (!insideReading) S.cash[cur] = (S.cash[cur] || 0) + signed;
  const tx = {
    id: uid(),
    date: dateVal + 'T12:00:00',
    type: cTxTp,
    amount: signed,
    currency: cur,
    reason: reason,
    note: note ? (reason + ': ' + note) : reason,
    _updatedAt: Date.now()
  };
  if (insideReading) tx._audit = true;
  S.cashTxs.unshift(tx);
  lg('cash', '—', cTxTp + ' (' + reason + '): ' + fc(am) + ' ' + cur + (insideReading ? ' (inside the ' + anchorDay + ' bank reading, balance untouched)' : ''));
  save();
  markDirty('dash');
  clMo('moCTx');
  renderC();
  toast(cTxTp + ' (' + reason + '): ' + fc(am) + (insideReading ? ' — dated inside the ' + anchorDay + ' bank reading; recorded, balance untouched' : ''), 'a-b');
}

/**
 * Label a cash row the classifier could not read.
 *
 * `txClass` is the ONE field that outranks every heuristic in capital.js, so this is the
 * owner overruling the machine — which is the correct direction, because he is the only one
 * who knows what a ₺500,000 deposit with an empty note actually was. Nothing is guessed on
 * his behalf and nothing is deleted; the row keeps its amount, date and note.
 */
export function setCashTxClass(e) {
  const el = e && e.target;
  if (!el) return;
  let id;
  try { id = JSON.parse(el.dataset.args || '[]')[0]; } catch (_err) { id = null; }
  const cls = el.value;
  if (!id || !cls) return;
  const valid = Object.values(TX_CLASS);
  if (valid.indexOf(cls) === -1) return toast('Unknown class', 'a-r');
  const tx = (S.cashTxs || []).find(t => t.id === id);
  if (!tx) return toast('Transaction not found', 'a-r');
  tx.txClass = cls;
  stampItem(tx);
  lg('cash', '—', 'Labelled ' + tx.type + ' ' + fByCur(tx.currency || 'USD', Math.abs(tx.amount || 0)) + ' as ' + cls);
  save();
  markDirty('dash');
  renderC();
  toast('Labelled as ' + cls.replace('_', ' ') + ' — capital figures recomputed', 'a-g');
}

// === CURRENCY CONVERSION ===
//
// One movement, two legs, one id. Until this existed the ledger held a dollar leaving and
// lira arriving as two unrelated rows in two currencies, so nothing downstream could tell
// money that had been CONVERTED from money that had gone MISSING. That is not a cosmetic
// gap: it is why a per-currency reconciliation booked every conversion as a surplus on one
// side and a shortfall on the other, and why the capital residual cannot yet be attributed.
// (audit 2026-08-02)

/** Paint the destination list from the classifier's own vocabulary. Never hand-typed in HTML. */
function _paintFxPurpose() {
  const sel = document.getElementById('fxcPurpose');
  if (!sel || sel.dataset.painted) return;
  sel.innerHTML = Object.values(FX_PURPOSE)
    .map(v => `<option value="${esc(v)}">${esc(FX_PURPOSE_LABEL[v] || v)}</option>`).join('');
  sel.dataset.painted = '1';
}

export function openFxConvert() {
  _paintFxPurpose();
  ['fxcFromAmt', 'fxcToAmt', 'fxcNote'].forEach(id => { const e = document.getElementById(id); if (e) e.value = ''; });
  document.getElementById('fxcFrom').value = 'USD';
  document.getElementById('fxcTo').value = 'TRY';
  document.getElementById('fxcDate').value = new Date().toISOString().slice(0, 10);
  calcFxConvert();
  document.getElementById('moFxConv').classList.add('active');
}

/** Live preview of the implied rate. Both amounts are entered by hand — the app never
 *  invents one leg from the other, because the spread between them IS the cost of the
 *  conversion and inferring it away would hide exactly what this record exists to expose. */
export function calcFxConvert() {
  const from = document.getElementById('fxcFrom').value;
  const to = document.getElementById('fxcTo').value;
  const a = parseFloat(document.getElementById('fxcFromAmt').value) || 0;
  const b = parseFloat(document.getElementById('fxcToAmt').value) || 0;
  const el = document.getElementById('fxcPreview');
  if (!el) return;
  if (from === to) { el.innerHTML = '<span style="color:var(--yel)">Pick two different currencies.</span>'; return; }
  if (!(a > 0) || !(b > 0)) { el.textContent = 'Enter both amounts.'; return; }
  el.innerHTML = `Implied rate <strong>1 ${esc(from)} = ${f(b / a, 4)} ${esc(to)}</strong>`;
}

export async function saveFxConvert() {
  const from = document.getElementById('fxcFrom').value;
  const to = document.getElementById('fxcTo').value;
  const a = parseFloat(document.getElementById('fxcFromAmt').value);
  const b = parseFloat(document.getElementById('fxcToAmt').value);
  const date = document.getElementById('fxcDate').value || new Date().toISOString().slice(0, 10);
  const note = (document.getElementById('fxcNote').value || '').trim();

  if (from === to) return toast('Pick two different currencies', 'a-r');
  if (!VALID_CURRENCIES.includes(from) || !VALID_CURRENCIES.includes(to)) return toast('Invalid currency', 'a-r');
  if (!(a > 0) || !(b > 0)) return toast('Both amounts are required', 'a-r');
  // A LEDGER THAT IS BEHIND REALITY IS NOT AN EMPTY ACCOUNT.
  //
  // This was a hard refusal, and on 2026-08-28 it refused the one record that would have
  // made the book correct. TRY 83,000 had left Garanti for IBKR and been converted to
  // USD 1,720; the ledger held TRY 78,994.14 because the September salary was already in
  // the bank but is dated day 1 of September, so nothing had counted it yet. The
  // conversion was a fact, the balance was stale, and the guard sided with the balance.
  //
  // `cashflow.js` already states the principle one screen over, about `free`: it can go
  // negative, that is a real state, and it must not be clamped -- or the one month that
  // needs the warning is the month that does not get it. A conversion that overdraws a
  // stale ledger is the same shape, and the next reconciliation corrects it.
  //
  // So the guard keeps the job it is good at -- catching an amount typed an order of
  // magnitude wrong -- and loses the power to veto a fact. It ASKS, and it says what the
  // balance will become, so a real typo is still obvious in the question.
  const _bal = S.cash[from] || 0;
  if (a > _bal && !await uiConfirm(
    `This converts ${fByCur(from, a)} but the ledger holds ${fByCur(from, _bal)}.\n\n`
    + `${from} cash will read ${fByCur(from, _bal - a)} until the next reconciliation.\n\n`
    + 'Record it anyway?',
    { title: `${from} ledger is behind the bank`, okText: 'Record conversion' })) return;

  // WHERE THE DOLLARS WENT. Asked at the one door where the owner knows the answer, rather
  // than inferred later from the currency pair -- which is what `cashflow.js` and
  // `integrity.js` used to do, and why every conversion read as the IBKR plan wire.
  const purposeEl = document.getElementById('fxcPurpose');
  const purpose = (purposeEl && purposeEl.value) || '';
  if (Object.values(FX_PURPOSE).indexOf(purpose) === -1) return toast('Pick a destination for the converted money', 'a-r');

  const fxId = uid();
  const rate = b / a;
  const label = note || `${fByCur(from, a)} → ${fByCur(to, b)}`;
  const leg = (cur, amount, other, dir) => stampItem({
    id: uid(), date: date + 'T12:00:00', type: 'fx_convert', amount, currency: cur,
    // Both legs are internal by construction: this is the owner's own money changing
    // denomination, never capital entering or leaving. Stated explicitly so no future
    // classifier has to infer it from the note.
    txClass: 'internal',
    fxId, fxRate: rate, fxPurpose: purpose, [dir]: other,
    note: `Convert ${from}→${to}: ${label}`,
  });
  S.cashTxs.unshift(leg(from, -a, to, 'fxTo'));
  S.cashTxs.unshift(leg(to, b, from, 'fxFrom'));
  S.cash[from] = (S.cash[from] || 0) - a;
  S.cash[to] = (S.cash[to] || 0) + b;

  lg('cash', '—', `Converted ${fByCur(from, a)} → ${fByCur(to, b)} @ ${f(rate, 4)}`);
  save();
  markDirty('dash');
  clMo('moFxConv');
  renderC();
  toast(`Converted ${fByCur(from, a)} → ${fByCur(to, b)} @ ${f(rate, 4)}`, 'a-g');
}

// === DELETE CASH TRANSACTION ===
export async function delCashTx(id) {
  const tx = S.cashTxs.find(t => t.id === id);
  if (!tx) return toast('Transaction not found', 'a-r');
  if (tx.type !== 'deposit' && tx.type !== 'withdraw') return toast('Only manual deposits/withdrawals can be deleted', 'a-r');
  // The button is hidden for these, and the rule is enforced here as well — a data-action can
  // be dispatched from anywhere, and this is the one that moves the balance. Naming the door is
  // the whole point: the row is not undeletable, it is deletable SOMEWHERE ELSE, where its twin
  // gets undone with it. (MOVE-003, 2026-08-14)
  const origin = cashTxOrigin(tx);
  if (origin.system) return toast(`This row mirrors ${origin.why}. Undo it at ${origin.door}.`, 'a-r');
  if (!await uiConfirm('Delete this ' + tx.type + ' of ' + fByCur(tx.currency || 'USD', Math.abs(tx.amount)) + '?', { title: 'Delete transaction', okText: 'Delete', danger: true })) return;
  // Reverse the cash impact — ONLY IF THERE WAS ONE. An `_audit` row is a mirror: it records
  // that money moved somewhere the balance already accounts for, and never moved the balance
  // itself. Reversing one subtracts money that was never added, silently, because an audit
  // row is invisible to the ledger sum that would otherwise catch it. Same asymmetry as
  // deleteOTI, one door over. (audit 2026-08-10)
  const cur = tx.currency || 'USD';
  if (!tx._audit) S.cash[cur] = (S.cash[cur] || 0) - tx.amount;
  // Remove transaction via softDelete (tombstoned) so a merge can't resurrect it and double-count the reversed balance
  softDelete('cashTxs', id);
  lg('cash', '—', 'Deleted ' + tx.type + ': ' + fByCur(cur, Math.abs(tx.amount)) + ' ' + cur);
  save();
  markDirty('dash');
  renderC();
  toast('Transaction deleted', 'a-b');
}

// === RESERVE FUND (multi-currency: TRY, USD, EUR, XAU, XAG, GBP) ===
const RF_CURRENCIES = ['TRY', 'USD', 'EUR', 'XAU', 'XAG', 'GBP'];

// ===== Money-shell renderer (mockup v0.2.1) — hero, chips, ring, KPIs, row
// summaries, event tape. Same numbers as before, new presentation. =====
function _caNeo() {
  const $ = id => document.getElementById(id);
  if (!$('caK1')) return;
  const cu = S.cash?.USD || 0, ct = cashTRYNow(), ce = S.cash?.EUR || 0;
  const usdU = cu, tryU = toUSD(ct, 'TRY'), eurU = toUSD(ce, 'EUR');
  const totU = usdU + tryU + eurU;
  const rtUSD = S.realized.reduce((a, r) => a + (realizedUSD(r)), 0);
  const rf = typeof S.reserveFund === 'object' ? S.reserveFund : { TRY: S.reserveFund || 0 };
  let rfTRY = 0; Object.entries(rf).forEach(([c, v]) => { rfTRY += rfToTRY(v || 0, c); });
  const divs = S.dividends || [];
  // Employer-match reclass (2026-07-21): match is earnings but NOT dividend income —
  // the Dividends cell shows real dividends; match rides the sub-line separately.
  const _dUSD = dividendUSD;
  const divUSD = divs.filter(d => d.kind !== 'match').reduce((s, d) => s + _dUSD(d), 0);
  const matchUSD = divs.filter(d => d.kind === 'match').reduce((s, d) => s + _dUSD(d), 0);

  // Hero: total + currency chips + context line
  $('caK1').textContent = fU(totU);
  $('caCcy').innerHTML = [
    ['TRY', '₺', ct], ['USD', '$', cu], ['EUR', '€', ce],
  ].map(([c, sym, nat]) => `<span class="mny-chip">${c} <b>${sym}${f(nat)}</b></span>`).join('')
    + `<span class="mny-chip">≈ <b>${fU(totU)}</b></span>`;
  const tryShare = totU > 0 ? Math.round(tryU / totU * 100) : 0;
  // "Earning nothing" is a claim about money he could deploy. Committed money is not idle, and
  // saying it is turns a correct figure into a wrong instruction — the deploy-window countdown
  // sits in the same sentence. When nothing has been reconciled the share is unknown, not zero.
  const _posture = tryCashPosture();
  // Both halves of the ratio off the SAME balance. The first version divided a numerator built
  // from `current` by a denominator built from the raw ledger, so the 60% threshold was applied
  // to a figure whose two halves were measured differently — 62% where the consistent answer
  // was 85%. A missing FX rate is a third state, not a zero: `tryShare` is 0 without a rate,
  // and printing "0% of liquidity sits in TRY" beside a chip reading ₺336,800 is two falsehoods.
  const _fxOk = !!S.fxRate;
  const _postureU = (_posture.ok && _fxOk) ? _posture.current / S.fxRate : null;
  const _liqU = _postureU != null ? (totU - tryU + _postureU) : null;
  const idleTRY = _posture.ok ? Math.max(0, _posture.free) : null;
  const idleShare = (idleTRY != null && _fxOk && _liqU > 0) ? Math.round((idleTRY / S.fxRate) / _liqU * 100) : null;
  const dEnd = new Date(); const daysLeft = new Date(dEnd.getFullYear(), dEnd.getMonth() + 1, 0).getDate() - dEnd.getDate();
  // Negative balances are a data-corruption tripwire — they must be visible in the
  // hero, never only in the hidden legacy #cSum block (audit HIGH-3)
  const negs = [];
  if (cu < 0) negs.push('USD ' + fU(cu));
  if (ct < 0) negs.push('TRY ' + fT(ct));
  if (ce < 0) negs.push('EUR €' + f(ce));
  $('caCtx').innerHTML = negs.length
    ? `<b style="color:var(--red)">⚠ Negative balance: ${negs.join(' · ')}</b> — review transactions.`
    : (totU > 0
      ? `${idleShare == null
          ? (!_fxOk
              ? `<b>No FX rate</b> — the TRY share of liquidity cannot be computed. `
              : `<b>${tryShare}% of liquidity sits in TRY.</b> How much of it is free is unknown ${_posture.why === 'no-month' ? `until ${esc(_posture.month || 'this month')} is opened in Income &amp; Expenses` : 'until you reconcile'}. `)
          : idleShare >= 60
            ? `<b>${idleShare}% of liquidity is uncommitted TRY</b> earning nothing${_posture.committed > 0 ? ` (${fT(_posture.committed)} of the ${fT(_posture.current)} is already owed or committed)` : ''}. `
            : _posture.free < 0
              ? `<b style="color:var(--ora)">TRY is over-committed by ${fT(-_posture.free)}</b> this month. `
              : ''}Monthly deploy window closes in <b>${daysLeft} day${daysLeft === 1 ? '' : 's'}</b>.`
      : 'No liquid cash tracked yet.');
  const meta = $('caMeta');
  if (meta) meta.innerHTML = `liquid <b>${fU(totU)}</b> · realized ${rtUSD >= 0 ? '+' : ''}${fU(rtUSD)} · reserve ${fU(reserveCoverageUSD())}`;

  // Reserve coverage = Hard Reserve (reserveFund pool) + Strategic Reserve (open TD
  // principal), against a target DERIVED from 6 months of forward essential outflow
  // (excl-karma). Target auto-adjusts as loans roll off — no hardcoded constant.
  const hardUSD = hardReserveUSD();
  const stratUSD = strategicReserveUSD();
  const coverageUSD = reserveCoverageUSD();
  const reserveTgtUSD = reserveTargetUSD();
  const covMonths = reserveTgtUSD > 0 ? coverageUSD / (reserveTgtUSD / 6) : 0;
  const rfPctRaw = reserveTgtUSD > 0 ? coverageUSD / reserveTgtUSD * 100 : 0; // true %, can exceed 100
  const rfPct = Math.min(rfPctRaw, 100);                                     // ring fill only
  const hardPct = reserveTgtUSD > 0 ? Math.round(hardUSD / reserveTgtUSD * 100) : 0;
  const ringFg = $('caRingFg');
  if (ringFg) ringFg.style.strokeDashoffset = String(339 - 339 * rfPct / 100);
  const ringPct = $('caRingPct');
  if (ringPct) ringPct.textContent = Math.round(rfPctRaw) + '%';
  try { if (window.SPK && window.SPK.liquidFill && $('caLiq')) window.SPK.liquidFill($('caLiq'), rfPct, 'var(--yel)'); } catch (_e) { /* legacy target hidden */ }

  // KPI chips + story sub-lines
  const k2 = $('caK2'); k2.className = 'v ' + pc(rtUSD); k2.textContent = (rtUSD >= 0 ? '+' : '') + fU(rtUSD);
  const closedTrades = S.realized.filter(r => !isInterestRealized(r));
  const best = closedTrades.slice().sort((a, b) => (b.pnlUSD || 0) - (a.pnlUSD || 0))[0];
  const s2 = $('caK2s'); if (s2) s2.textContent = `${closedTrades.length} closed trade${closedTrades.length === 1 ? '' : 's'}${best && best.pnlUSD > 0 ? ` · best ${best.ticker} +${fU(best.pnlUSD)}` : ''}`;
  $('caK4').textContent = fU(divUSD);
  const lastDiv = divs.slice().sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0];
  const s4 = $('caK4s'); if (s4) s4.textContent = matchUSD > 0
    ? `+ employer match ${fU(matchUSD)} — earnings, not passive`
    : (lastDiv ? `last: ${lastDiv.ticker} ${fU(dividendUSD(lastDiv))} · ${lastDiv.date || ''}` : 'no dividends yet');
  $('caK3').textContent = fU(coverageUSD);
  const s3 = $('caK3s'); if (s3) s3.textContent = `${covMonths.toFixed(1)} mo · ${Math.round(rfPctRaw)}% of ${fU(reserveTgtUSD)} target · hard ${fU(hardUSD)} + strat ${fU(stratUSD)}`;
  const k5 = $('caK5'); if (k5) k5.textContent = (idleShare == null ? tryShare : idleShare) + '%';
  const s5 = $('caK5s'); if (s5) s5.textContent = idleShare == null
    ? (!_fxOk ? 'no FX rate — share unavailable'
       : `${tryShare}% held in TRY · free share unknown (${_posture.why === 'no-month' ? 'month not opened' : 'not reconciled'})`)
    : _posture.free < 0
      ? `over-committed by ${fT(-_posture.free)} — ${tryShare}% of liquidity is TRY`
      : idleShare >= 60
        ? `uncommitted — inflation drag · ${tryShare}% of liquidity is TRY`
        // "of it" read as "of the TRY", but idleShare is a share of TOTAL liquidity — both
        // halves of the ratio are measured against the same base on purpose. They only look
        // alike here because TRY is 95% of liquidity; with real USD cash the sentence lies.
        : `${tryShare}% held in TRY · ${idleShare}% of liquidity is free TRY`;

  // Ledger row summaries
  const rSum = (id, html) => { const el = $(id); if (el) el.innerHTML = html; };
  rSum('caRfSum', `<span class="gold">${fT(rfTRY)}</span> · hard tier ${fU(hardUSD)} · ${hardPct}% of target`);
  rSum('caDivSum', `<span class="pos">${fU(divUSD)} net</span> · ${divs.length} payment${divs.length === 1 ? '' : 's'}`);
  {
    // The collapsed row is a header, not a report. The first version put the whole four-figure
    // TRY story here and it ran under the row title — the owner saw overlapping text before he
    // saw any of the numbers. Header: one figure. Panel: the working.
    const p = projectedCashTRY();
    const n = (S.cashTxs || []).length;
    // `ok:false` has two causes and they are not the same sentence. Saying "not reconciled" on
    // the 1st of a month — before the new month exists in Income & Expenses — contradicted the
    // panel one line below it, which said the opposite.
    rSum('caTxSum', p.ok
      ? `${n} tx · TRY ${fT(p.current)}`
      : p.at ? `${n} tx · TRY ${fT(p.current)} · ${esc(p.month || '')} not opened`
             : `${n} tx · TRY not reconciled`);

    const slot = document.getElementById('caTryProj');
    if (slot) {
      if (!p.ok) {
        slot.innerHTML = `<div class="gls-soft" style="padding:8px 10px;margin:0 0 8px;border:1px solid var(--bd);border-radius:8px;font-size:11px;color:var(--t2)">${p.at
          ? `Reconciled ${ds(p.at)}, but ${esc(p.month || 'this month')} has no cash-flow record yet — nothing to project against. Open it in Income &amp; Expenses.`
          : 'TRY cash has never been checked against the banks. <b>Reconcile TRY</b> records what they say; until then there is no projection, only a running figure.'}</div>`;
      } else {
        const cell = (l, v, col) => `<div class="neo-cell flat"><div class="l">${l}</div><div class="v"${col ? ` style="color:${col}"` : ''}>${v}</div></div>`;
        slot.innerHTML = `<div class="sg" style="margin:0 0 10px">
          ${cell('Now', fT(p.current))}
          ${cell('Still due', p.remaining ? fT(-p.remaining) : fT(0), p.remaining ? 'var(--red)' : null)}
          ${cell('Plan transfer left', p.commitment ? fT(-p.commitment) : fT(0), p.commitment ? 'var(--ora)' : null)}
          ${cell(esc(p.month) + ' end', fT(p.projected), p.projected < 0 ? 'var(--red)' : 'var(--grn)')}
        </div>
        <div style="font-size:10px;color:var(--t3);margin:0 0 10px">${p.verified == null
          ? `Banks last read ${ds(p.at)}; the amount was not recorded. Reconcile again to capture it.`
          : `Banks last read ${ds(p.at)} at ${fT(p.verified)}.`} Ledger ${fT(p.ledger)}${p.salaryIn ? ` + ${fT(p.salaryIn)} income` : ''}${p.paidOut ? ` − ${fT(p.paidOut)} paid` : ''}${p.transferred ? ` · ${fT(p.transferred)} of the plan already transferred` : ''}.</div>`;
      }
    }
  }
  rSum('caRlSum', `<span class="${rtUSD >= 0 ? 'pos' : 'neg'}">${rtUSD >= 0 ? '+' : ''}${fU(rtUSD)}</span> · ${S.realized.length} closed`);
  rSum('caChSum', `${S.realized.length ? 'FY track' : 'no closed trades yet'}`);

  // Money tape: recent dividends, exits, cash tx (newest 8, real events only)
  const tape = [];
  divs.slice(-3).forEach(d => tape.push(`<b>DIV</b> <span class="in">+${fU(dividendUSD(d))} net</span> ${esc(d.ticker || '')} · ${esc(d.date || '')}`));
  S.realized.slice(0, 3).forEach(r => { const p = realizedUSD(r); tape.push(`<b>EXIT</b> <span class="${p >= 0 ? 'in' : 'out'}">${p >= 0 ? '+' : ''}${fU(p)}</span> ${esc(r.ticker || '')} · ${esc(ds(r.date))}`); });
  (S.cashTxs || []).slice(0, 3).forEach(t => tape.push(`<b>${t.type === 'deposit' ? 'IN' : 'OUT'}</b> <span class="${t.type === 'deposit' ? 'in' : 'out'}">${t.type === 'deposit' ? '+' : '−'}${fByCur(t.currency || 'USD', Math.abs(t.amount || 0))}</span> ${esc((t.note || '').slice(0, 24))} · ${esc(ds(t.date))}`));
  if (S.fxRate) tape.push(`<b>FX</b> USD/TRY ${f(S.fxRate)} <span class="soon">live</span>`);
  const tapeEl = $('caTape'), tapeWrap = $('caTapeWrap');
  if (tapeEl && tapeWrap) {
    if (tape.length >= 3) {
      tapeEl.innerHTML = [...tape, ...tape].map(t => `<span class="mny-ti">${t}</span>`).join('');
      tapeEl.style.animationDuration = Math.max(46, tape.length * 9) + 's';
      tapeWrap.style.display = '';
    } else { tapeWrap.style.display = 'none'; }
  }
}

/** Money-shell ledger row toggle (shared by cash/cashflow/bonus pages). */
export function mnyToggle(id) {
  const row = document.getElementById(id);
  if (!row) return;
  row.classList.toggle('open');
  // charts inside a freshly-opened row need a resize kick to pick up real width
  if (row.classList.contains('open') && row.querySelector('canvas')) {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 380);
  }
}

// Helper: get RF object safely
function _rf() {
  if (!S.reserveFund || typeof S.reserveFund !== 'object') S.reserveFund = { TRY: 0, USD: 0, EUR: 0, XAU: 0, XAG: 0, GBP: 0 };
  return S.reserveFund;
}

// Helper: convert RF currency amount to TRY
export function rfToTRY(amount, currency) {
  if (!amount) return 0;
  // `|| 1` valued the USD and EUR reserve legs at 1 TRY = 1 unit. hardReserveUSD() already
  // refuses without a rate; these legs must not fabricate one either. (audit 2026-08-01)
  //
  // ⚠ AND THE GBP LEG WENT ON FABRICATING ONE FOR ANOTHER SIXTEEN DAYS (plan #6, 2026-08-17).
  // It read `amount * fxR * 1.27` — a GBP/USD rate hard-coded in a comment as "approximate".
  // **`S.fxGBPUSD` is a real field**: `prices.js:296` writes it (`1 / gbpRate`), it is in
  // `SCALAR_FIELDS`, and it joined the sync contract on 2026-08-16. The policy is stated one file
  // over, at `prices.js:368` — *"Returns null when a GBP quote cannot be converted (no fxGBPUSD
  // yet) — no price is better than a wrong one."* This line did the opposite of its own
  // codebase's documented rule, next to a comment saying not to.
  //
  // ── AN UNCONVERTIBLE LEG RETURNS `null` NOW, NOT 0 (owner's decision, 2026-08-17) ─────────
  //
  // 0 is a claim that the leg is EMPTY. The truth is that it is UNKNOWN, and the difference was
  // worth ₺115,271 of reserve fund on a cleared price map — reported as nothing, with nothing on
  // screen saying so. Theme #32.
  //
  // ⚠ **RETURNING `null` ON ITS OWN ACCOMPLISHES ALMOST NOTHING, AND MEASURING THAT IS THE ONLY
  // REASON THIS CHANGE IS SAFE.** `null` is not `NaN`: `x += null` is `x + 0`, and
  // `null / 47.88` is `0`. So every summing caller behaves EXACTLY as before — the change is
  // invisible at five of the eleven sites and would have shipped as "fixed". What actually makes
  // the difference visible is the work at the call sites, which is where it now lives:
  //
  //   `rfAction` (money mover)  REFUSES the deposit/withdraw — see the guard there
  //   `renderRF`               shows the leg and the total as an em dash, via `rfTotalTRY`
  //   `dashboard.metal()`      already turns null into an em dash by construction
  //   `integrity.js`           `reserve.unpriced` — the check that makes it findable
  //   the aggregates           still sum as 0, by the policy `hardReserveUSD` already documents
  //
  // A zero BALANCE is still 0, not null: the `if (!amount)` above catches it first. Nothing is
  // unknown about a leg he does not hold.
  const fxR = S.fxRate > 0 ? S.fxRate : null;
  const fxE = S.fxRateEUR > 0 ? S.fxRateEUR : null;
  const fxG = S.fxGBPUSD > 0 ? S.fxGBPUSD : null;
  if ((currency === 'USD' && fxR == null) || (currency === 'EUR' && fxE == null)) return null;
  if (currency === 'GBP' && (fxR == null || fxG == null)) return null;
  switch (currency) {
    case 'TRY': return amount;
    case 'USD': return amount * fxR;
    case 'EUR': return amount * fxE;
    case 'GBP': return amount * fxG * fxR;   // GBP -> USD -> TRY, both legs measured
    case 'XAU': {
      const p = S.prices?.['XAU']; // XAU price is in TRY per gram
      return p && p.price > 0 ? amount * p.price : null;
    }
    case 'XAG': {
      const p = S.prices?.['XAG']; // XAG price is in TRY per gram
      return p && p.price > 0 ? amount * p.price : null;
    }
    default: return null;                    // an unknown currency is unknown, not empty
  }
}

/**
 * The whole reserve pool in TRY, **and which legs it could not value.**
 *
 * The reason this exists rather than a bare sum: `null` coerces to 0 in `+=`, so a caller that
 * sums `rfToTRY` cannot tell a missing gold price from no gold. It has to be told separately,
 * and once it is told, it can say so. `unknown` is the list of currency codes he holds a balance
 * in and the app cannot price.
 */
export function rfTotalTRY(rf) {
  const pool = rf || _rf();
  let total = 0;
  const unknown = [];
  RF_CURRENCIES.forEach(c => {
    const v = pool[c] || 0;
    if (!v) return;                          // a leg he does not hold is not a gap
    const t = rfToTRY(v, c);
    if (t == null) unknown.push(c); else total += t;
  });
  return { total, unknown };
}

/**
 * CANONICAL reserve coverage — the single source of truth every surface (Money
 * hero, Dashboard bar, Bonus scan, Goals) must call. Never re-sum reserve inline;
 * add any new reserve component HERE only and all consumers inherit it.
 *   hardReserveUSD()     = whole reserveFund pool (every key, metal-aware)
 *   reserveCoverageUSD() = Hard Reserve + Strategic Reserve (open TD principal)
 */
export function hardReserveUSD() {
  const rf = _rf();
  // `S.fxRate || 1` computed the entire reserve at 1 TRY = 1 USD when FX was missing,
  // inflating coverage ~40x and with it the reserve gap that the Bonus planner sizes its
  // first tranche from. 0 is the honest answer: no rate, no USD figure. The integrity
  // watchdog reports the missing rate separately. (audit 2026-08-01)
  const fx = S.fxRate;
  if (!(fx > 0)) return 0;
  let tryTot = 0;
  Object.entries(rf).forEach(([c, v]) => { tryTot += rfToTRY(v || 0, c); });
  return tryTot / fx;
}
export function reserveCoverageUSD() {
  return hardReserveUSD() + strategicReserveUSD();
}

export async function rfAction(type) {
  // Build currency select prompt
  const curStr = await uiPrompt('Currency? (TRY/USD/EUR/XAU/XAG/GBP):', 'TRY', { title: (type === 'deposit' ? 'Deposit to' : 'Withdraw from') + ' Reserve Fund' });
  if (!curStr) return;
  const cur = curStr.trim().toUpperCase();
  if (!RF_CURRENCIES.includes(cur)) return toast('Invalid currency. Use: ' + RF_CURRENCIES.join(', '), 'a-r');

  const unit = cur === 'XAU' || cur === 'XAG' ? 'grams' : cur;
  const amtStr = await uiPrompt('Amount (' + unit + '):', '', { title: 'Reserve Fund — amount', okText: 'Next', inputType: 'number' });
  if (!amtStr) return;
  const amt = parseFloat(amtStr);
  if (!amt || amt <= 0) return toast('Invalid amount', 'a-r');

  const rf = _rf();
  if (type === 'withdraw' && (rf[cur] || 0) < amt) return toast('Insufficient RF balance in ' + cur, 'a-r');

  const note = (await uiPrompt('Note (optional):', '', { title: 'Reserve Fund — note', okText: 'Continue' })) || '';
  const signed = type === 'deposit' ? amt : -amt;
  rf[cur] = (rf[cur] || 0) + signed;

  // Ask source: confirm = from app cash, cancel = external
  if (!S.cash) S.cash = {};
  const fromCash = type === 'deposit'
    ? await uiConfirm('Confirm = deduct from app cash · Cancel = external source', { title: 'Deposit source', okText: 'From app cash' })
    : await uiConfirm('Confirm = credit to app cash · Cancel = external', { title: 'Withdraw destination', okText: 'To app cash' });
  // ⚠ ONE DERIVATION, USED TWICE. These two lines were computed here AND again forty lines down
  // when the `cashTxs` row is built. Two `rfToTRY` calls for one decision is the shape that made
  // MOVE-005 a pump: a repair whose input is re-read after the fact. Nothing awaits between them
  // today, so they cannot disagree today — which is exactly the kind of "cannot" this codebase
  // has been wrong about twice this week.
  let cashCur = null, cashAmt = null;
  if (fromCash) {
    cashCur = ['TRY', 'USD', 'EUR'].includes(cur) ? cur : 'TRY';
    cashAmt = ['TRY', 'USD', 'EUR'].includes(cur) ? amt : rfToTRY(amt, cur);
    // ⚠⚠ THE MONEY MOVER, AND BOTH CONTRACTS WERE WRONG HERE (2026-08-17).
    //
    // Depositing 5 g of gold "from app cash" has to deduct what 5 g is WORTH. With no XAU price
    // the old code got `cashAmt = 0` and did `S.cash.TRY -= 0` — the gold arrived in the reserve
    // fund and **the cash side never happened**, silently, under a green toast. Returning `null`
    // instead does not fix it: `S.cash.TRY - null` is also `S.cash.TRY - 0`. A contract change
    // that leaves a money mover reading the same number is not a fix.
    //
    // There is no honest figure to write, so nothing is written. The reserve leg applied a few
    // lines up is rolled back first — the same rollback the insufficient-funds branch does.
    if (cashAmt == null || !(cashAmt > 0)) {
      rf[cur] -= signed;
      return toast(`No ${cur} price available — cannot value this against cash. Refresh prices, or record it as an external ${type}.`, 'a-r', { duration: 9000 });
    }
    if (type === 'deposit') {
      if ((S.cash[cashCur] || 0) < cashAmt) {
        rf[cur] -= signed; // rollback
        return toast('Insufficient ' + cashCur + ' cash (' + f(S.cash[cashCur] || 0) + ' available)', 'a-r');
      }
      S.cash[cashCur] = (S.cash[cashCur] || 0) - cashAmt;
    } else {
      S.cash[cashCur] = (S.cash[cashCur] || 0) + cashAmt;
    }
  }

  if (!S.rfTxs) S.rfTxs = [];
  // PROVENANCE ON THE FORWARD PATH. Until 2026-08-14 this row was
  // `{ id, date, type, amount, currency, note }` and recorded NOTHING about whether app cash
  // funded it — so `rfDeleteTx` reversed the reserve leg alone and left the cash side where it
  // was. `saveAddLot` learned this on 08-07 (`funded: 'cash'`), v50 learned it for dividends and
  // v51 for liability payments; this was the last one still missing it. (MOVE-002)
  const _rfRow = { id: uid(), date: now(), type, amount: signed, currency: cur, note, funded: fromCash ? 'cash' : 'external' };
  S.rfTxs.unshift(_rfRow);

  // Record in cashTxs only when app cash was affected
  if (fromCash) {
    // `cashCur` / `cashAmt` come from the single derivation above — the guard there has already
    // returned if either could not be established.
    const _cashRow = stampItem({
      id: uid(), date: now(), type: 'rf_' + type,
      amount: type === 'deposit' ? -cashAmt : cashAmt, currency: cashCur,
      note: 'Reserve Fund ' + type + ' (' + f(amt) + ' ' + cur + ')' + (note ? ': ' + note : '')
    });
    S.cashTxs.unshift(_cashRow);
    _rfRow._cashTxId = _cashRow.id;
  }

  lg('cash', 'RF', type + ' ' + f(amt) + ' ' + cur + ' → Reserve Fund');
  save();
  markDirty('dash');
  renderC();
  toast('Reserve Fund ' + type + ': ' + f(amt) + ' ' + cur, 'a-b');
}

export function renderRF() {
  const rf = _rf();
  const fxR = S.fxRate > 0 ? S.fxRate : null;

  // ⚠ A TOTAL THAT IS SHORT A LEG IS NOT A TOTAL (2026-08-17). This was
  // `totalTRY += rfToTRY(rf[c] || 0, c)` over every currency — and since `null` coerces to 0 in
  // `+=`, an unpriceable gold leg simply vanished from the headline. The figure stayed crisp and
  // stopped being true. `rfTotalTRY` reports WHICH legs it could not value, so the headline can
  // decline instead of rounding a gap down to nothing.
  const { total: totalTRY, unknown } = rfTotalTRY(rf);
  const rfIncomplete = unknown.length > 0;
  // No rate, no USD headline. fU(null) renders an em dash. (audit 2026-08-01)
  const totalUSD = (fxR && !rfIncomplete) ? totalTRY / fxR : null;

  // Render per-currency balances
  const rfEl = document.getElementById('rfBalances');
  if (rfEl) {
    const lines = RF_CURRENCIES.filter(c => (rf[c] || 0) !== 0).map(c => {
      const val = rf[c];
      const tryEq = rfToTRY(val, c);
      const label = c === 'XAU' || c === 'XAG' ? f(val, 2) + 'g ' + c : fByCur(c, val) + ' ' + c;
      return `<div style="display:flex;justify-content:space-between;font-size:11px"><span style="color:var(--t2)">${label}</span><span style="color:var(--t3)">≈ ${fT(tryEq)}</span></div>`;
    });
    rfEl.innerHTML = lines.length ? lines.join('') : '<div class="neo-empty">No reserves</div>';
  }

  const totalEl = document.getElementById('rfTotalTRY');
  // `fT(null)` is an em dash — the same vocabulary the USD headline has used since 2026-08-01.
  if (totalEl) {
    totalEl.textContent = fT(rfIncomplete ? null : totalTRY);
    totalEl.title = rfIncomplete
      ? `No price for ${unknown.join(', ')} — the total would understate the pool. Refresh prices.`
      : '';
  }
  const totalUsdEl = document.getElementById('rfTotalUSD');
  if (totalUsdEl) totalUsdEl.textContent = fU(totalUSD);

  const txs = (S.rfTxs || []).slice(0, 20);
  const txBody = document.getElementById('rfTxBody');
  if (txBody) txBody.innerHTML = txs.length ? txs.map(t => {
    const cur = t.currency || 'TRY';
    const dispAmt = cur === 'XAU' || cur === 'XAG' ? f(Math.abs(t.amount), 2) + 'g' : fByCur(cur, Math.abs(t.amount));
    // WHERE THE MONEY CAME FROM, ON THE PAGE. v52 stamps `funded` and links the ledger leg, and
    // `rfDeleteTx` reverses both legs only for a cash-funded row — but none of that was visible,
    // so he could not check the app's belief against his own memory. That check is exactly what
    // found the ₺34,000 counted twice: a row noted "try cash" with no cash leg behind it.
    //
    // Three states, and the third is the honest one: rows written before v52 that it refused to
    // guess about. Delete treats them as external, which is right for the four "Akbank" rows and
    // is the assumption being shown rather than hidden.
    const _fund = t.funded === 'cash'
      ? '<span class="badge" style="font-size:9px;background:rgba(167,139,250,.18);color:var(--pur,#a78bfa)" title="App cash was debited for this. Deleting it returns the money.">from cash</span>'
      : t.funded === 'external'
        ? '<span class="badge" style="font-size:9px;opacity:.75" title="Funded outside the app. Deleting it moves the reserve only.">external</span>'
        : '<span class="badge" style="font-size:9px;opacity:.5" title="Written before v52, and the migration refused to guess. Deleting it moves the reserve only.">unrecorded</span>';
    // Edit is refused for a cash-funded row (it would move the reserve without moving the cash),
    // so the button says so instead of failing on click.
    const _edit = t.funded === 'cash'
      ? '<span class="badge" style="font-size:9px;opacity:.5" title="Delete and re-enter — that path returns the cash">no edit</span>'
      : `<button class="btn btn-s" data-action="rfEditTx" data-args='["${t.id}"]' style="font-size:9px;padding:2px 6px">Edit</button>`;
    return `<tr><td>${ds(t.date)}</td><td><span class="badge b-${t.type === 'deposit' ? 'open' : 'stopped'}">${t.type}</span></td><td style="color:var(--t3);font-size:10px">${cur}</td><td class="tr ${t.amount >= 0 ? 'pos' : 'neg'}">${dispAmt}</td><td>${_fund}</td><td style="color:var(--t2)">${esc(t.note || '')}</td><td style="white-space:nowrap">${_edit} <button class="btn btn-s" data-action="rfDeleteTx" data-args='["${t.id}"]' style="font-size:9px;padding:2px 6px;color:var(--red)">Del</button></td></tr>`;
  }).join('') : '<tr><td colspan="7" class="neo-empty">No transactions</td></tr>';

  renderStrategicReserve();
}

/**
 * Strategic Reserve row (Money page): open time deposits framed as deployable
 * reserve. Principal = reserve coverage; accrued interest books to earnings, not
 * here. Management (rate change / close) stays on the Trades page (option A).
 */
export function renderStrategicReserve() {
  const tds = (S.trades || []).filter(t => t.type === 'TimeDeposit' && t.td && t.status !== 'Closed');
  const sumUSD = tds.reduce((s, t) => s + toUSD(t.td.principal || 0, t.currency || 'TRY'), 0);
  const accrUSD = tds.reduce((s, t) => {
    const a = tdAccrued(t.td); return s + toUSD(a ? (a.accruedNet || 0) : 0, t.currency || 'TRY');
  }, 0);
  const sumEl = document.getElementById('caSrSum');
  if (sumEl) sumEl.innerHTML = tds.length
    ? `<span class="gold">${fU(sumUSD)}</span> · ${tds.length} deposit${tds.length === 1 ? '' : 's'} · accrued <span class="pos">+${fU(accrUSD)}</span>`
    : '<span style="color:var(--t3)">no strategic reserve</span>';
  const body = document.getElementById('srListBody');
  if (!body) return;
  body.innerHTML = tds.length ? tds.map(t => {
    const cur = t.currency || 'TRY';
    const a = tdAccrued(t.td);
    const accr = a ? (a.accruedNet || 0) : 0;
    const rate = t.td.annualRate != null ? t.td.annualRate : '—';
    const since = t.td.startDate ? ds(t.td.startDate).split(',')[0] : '—';
    const isDaily = (t.td.kind || 'daily') === 'daily';
    const actCell = isDaily
      ? `<button class="btn btn-s" data-action="closeDailyTd" data-args='["${t.id}"]' style="font-size:9px;padding:2px 6px;color:var(--yel)">Break → Cash</button>`
      : `<span style="font-size:10px;color:var(--t3)">manage on Trades</span>`;
    return `<tr>
      <td><b>${esc(t.ticker)}</b><div style="font-size:10px;color:var(--t3)">${esc(t.td.kind || 'daily')} · breakable</div></td>
      <td style="color:var(--t2)">${esc(t.td.bank || '—')}</td>
      <td class="tr">${fByCur(cur, t.td.principal || 0)}<div style="font-size:10px;color:var(--t3)">${fU(toUSD(t.td.principal || 0, cur))}</div></td>
      <td class="tr pos">${rate}%</td>
      <td class="tr pos">+${fByCur(cur, accr)}<div style="font-size:10px;color:var(--t3)">→ earnings</div></td>
      <td style="color:var(--t3)">${since}</td>
      <td style="white-space:nowrap">${actCell}</td>
    </tr>`;
  }).join('') : '<tr><td colspan="7" class="neo-empty">No strategic reserve — open a daily deposit on the Trades page.</td></tr>';
}

// === RF EDIT / DELETE ===
export async function rfEditTx(id) {
  const tx = (S.rfTxs || []).find(t => t.id === id);
  if (!tx) return toast('Transaction not found', 'a-r');
  // Worse than the delete path, and refused rather than half-fixed. This adjusts the reserve
  // balance for the new amount and currency and touches NEITHER `S.cash` NOR the `rf_*` ledger
  // row — so editing a cash-funded ₺50,000 down to ₺10,000 moved ₺40,000 out of the reserve
  // while cash stayed ₺50,000 lower. Re-pricing a two-legged move through a chain of prompts
  // that can each be cancelled, across a currency change the code was never exercised on, is a
  // worse instrument than deleting and re-entering — and the delete path now returns the cash.
  if (tx.funded === 'cash') {
    return toast(
      'This reserve deposit was funded from app cash. Editing it here would move the reserve '
      + 'without moving the cash. Delete it — that returns the money — and enter it again.', 'a-r');
  }

  const rf = _rf();
  const oldCur = tx.currency || 'TRY';
  const oldAmt = Math.abs(tx.amount);

  // Reverse old effect on RF balance
  rf[oldCur] = (rf[oldCur] || 0) - tx.amount;

  // Edit fields
  const newCurStr = await uiPrompt('Currency (' + RF_CURRENCIES.join('/') + '):', oldCur, { title: 'Edit RF transaction' });
  if (!newCurStr) { rf[oldCur] = (rf[oldCur] || 0) + tx.amount; return; } // rollback
  const newCur = newCurStr.trim().toUpperCase();
  if (!RF_CURRENCIES.includes(newCur)) { rf[oldCur] = (rf[oldCur] || 0) + tx.amount; return toast('Invalid currency', 'a-r'); }

  const newAmtStr = await uiPrompt('Amount:', oldAmt.toString(), { title: 'Edit RF transaction', okText: 'Next', inputType: 'number' });
  if (!newAmtStr) { rf[oldCur] = (rf[oldCur] || 0) + tx.amount; return; }
  const newAmt = parseFloat(newAmtStr);
  if (!newAmt || newAmt <= 0) { rf[oldCur] = (rf[oldCur] || 0) + tx.amount; return toast('Invalid amount', 'a-r'); }

  const newNote = await uiPrompt('Note:', tx.note || '', { title: 'Edit RF transaction', okText: 'Save' });

  // Apply new values
  const newSigned = tx.type === 'deposit' ? newAmt : -newAmt;
  tx.amount = newSigned;
  tx.currency = newCur;
  if (newNote !== null) tx.note = newNote;
  rf[newCur] = (rf[newCur] || 0) + newSigned;

  lg('cash', 'RF', 'Edited RF tx: ' + f(newAmt) + ' ' + newCur);
  save();
  markDirty('dash');
  renderC();
  toast('RF transaction updated', 'a-b');
}

export async function rfDeleteTx(id) {
  const tx = (S.rfTxs || []).find(t => t.id === id);
  if (!tx) return toast('Transaction not found', 'a-r');
  const cur = tx.currency || 'TRY';

  // BOTH LEGS, OR NEITHER. This reversed the reserve balance alone. For an EXTERNAL deposit that
  // is correct and complete — the money never touched app cash. For a CASH-funded one it is the
  // one-legged write pointing backwards: the reserve gives the money back, app cash never
  // receives it, and total wealth quietly drops by the amount while `S.cash[cur] === Σ cashTxs`
  // still holds, so B3 stays green throughout. (MOVE-002)
  let _cashLeg = null;
  if (tx.funded === 'cash') {
    const rows = (S.cashTxs || []).filter(c => c && c.id === tx._cashTxId);
    if (rows.length !== 1) {
      return toast(
        'This reserve deposit was funded from app cash but its ledger row cannot be identified, '
        + 'so reversing it would move the reserve without returning the cash. Restore the link or undo it by hand.',
        'a-r');
    }
    _cashLeg = rows[0];
  }

  const _msg = 'Delete this RF transaction? (' + cur + ' ' + f(Math.abs(tx.amount)) + ')'
    + (_cashLeg
      ? ' — ' + f(Math.abs(_cashLeg.amount)) + ' ' + (_cashLeg.currency || 'TRY') + ' goes back to app cash.'
      : ' — externally funded, so no app cash is involved.');
  if (!await uiConfirm(_msg, { title: 'Delete RF transaction', okText: 'Delete', danger: true })) return;

  // Reverse effect on RF balance
  const rf = _rf();
  rf[cur] = (rf[cur] || 0) - tx.amount;

  // …and the cash leg with it, when there was one.
  if (_cashLeg) {
    const lc = _cashLeg.currency || 'TRY';
    S.cash[lc] = (S.cash[lc] || 0) - (Number(_cashLeg.amount) || 0);
    tombstoneId(_cashLeg.id);
    S.cashTxs = S.cashTxs.filter(c => c.id !== _cashLeg.id);
  }

  // Remove via softDelete (tombstoned; rfTxs is merge-synced — resurrection would double-count the reversed RF balance)
  softDelete('rfTxs', id);

  lg('cash', 'RF', 'Deleted RF tx: ' + f(Math.abs(tx.amount)) + ' ' + cur + (_cashLeg ? ' (cash returned)' : ''));
  save();
  markDirty('dash');
  renderC();
  toast('RF transaction deleted', 'a-b');
}

// === HELPER: Log to history ===
function lg(a, t, d) {
  S.history.unshift({ ts: now(), action: a, ticker: t, details: d });
  if (S.history.length > 1000) S.history.pop();
}

// save() is now imported from '../core/state.js' — no local stub needed
